import z from "zod";

/**
 * The longest a comment may be, on every item of every instrument alike.
 *
 * Stated once and in the instrument rather than per collector, so a document
 * arriving over the API is bounded exactly as one typed into a screen is.
 */
export const COMMENT_MAX_LENGTH = 4000;

/**
 * The annotation holding what a group or an item means, and, for an item,
 * each of its answer options, beside the schema it describes.
 *
 * `x-` because JSON Schema reserves that prefix for keywords it will never
 * define, and treats them as annotations, so the meaning travels with the
 * schema without affecting what is valid.
 */
export const MEANING_KEYWORD = "x-meaning";

/**
 * The annotation saying what kind of domain a group is; `x-` for the reason
 * `MEANING_KEYWORD` is.
 */
export const CATEGORY_KEYWORD = "x-category";

/**
 * The kinds of domain a strengths-and-needs instrument sorts its groups into,
 * as CANS states one for each of its domains:
 *
 * - `needs`: the young person's needs.
 * - `strengths`: the young person's strengths.
 * - `history`: what has happened to the young person, rated over their
 *   lifetime.
 * - `caregiver`: the needs and resources of the people caring for them.
 *
 * A list of our own rather than one each instrument names, because a reader
 * lays out each kind differently, and cannot lay out a kind it has never met.
 */
export const GROUP_CATEGORIES = [
  "needs",
  "strengths",
  "history",
  "caregiver",
] as const;

export type GroupCategory = (typeof GROUP_CATEGORIES)[number];

/** Text in each language it exists in, keyed by language tag. */
export type LanguageMap = Readonly<Record<string, string>>;

/**
 * The canonical definition of what something in an instrument means, for any
 * reader, whatever a collector showed.
 */
export type Meaning = {
  readonly title: LanguageMap;
  readonly description?: LanguageMap;
};

/**
 * What one answer option of a closed item means (`docs/glossary/answer.md`),
 * for that item alone.
 */
export type AnswerOptionMeaning<Value> = Meaning & { readonly value: Value };

/**
 * What an item means, and, where its response is a closed set, what each of
 * its values means: an item may give each value its own meaning, as CANS
 * anchors every level per item.
 *
 * A closed set is one of particular strings and numbers, whether one value or
 * several; a response of a whole JSON type (any string, any number) has no
 * values to describe, and nothing else is modelled as a closed set.
 *
 * The answer options' key is `answers`, not `answerOptions`, because every
 * published instrument's digest covers it.
 */
export type ItemMeaning<Value> = Meaning &
  ([Value] extends [string | number]
    ? string extends Value
      ? { readonly answers?: never }
      : number extends Value
        ? { readonly answers?: never }
        : { readonly answers: readonly AnswerOptionMeaning<Value>[] }
    : { readonly answers?: never });

/**
 * The annotation marking an item as a supplement to another in its group: an
 * answer that exists only to add to that one, as a comment does. `to` names the
 * item it adds to, and `as` what kind of supplement it is, which decides its key
 * and how it is handled. An `x-` keyword for the reason `MEANING_KEYWORD` is:
 * JSON Schema treats it as an annotation, so declaring it changes nothing a
 * document may hold.
 */
export const SUPPLEMENT_KEYWORD = "x-supplement";

/** The kinds of supplement an item may have. */
export const SUPPLEMENT_KINDS = ["comment"] as const;

export type SupplementKind = (typeof SUPPLEMENT_KINDS)[number];

/** What an item's `SUPPLEMENT_KEYWORD` says: which item it adds to, and as what. */
export type Supplement = { readonly to: string; readonly as: SupplementKind };

/**
 * What a comment's key ends in. A comment is keyed by its item's key with this
 * appended, so a reader that never sees the schema, such as the recipient of an
 * export with nowhere to say one answer is about another, can still pair them.
 */
export const COMMENT_SUFFIX = "_comment";

export type CommentKeyOf<Key extends string> = `${Key}${typeof COMMENT_SUFFIX}`;

/** The key of the comment on an item. */
export const commentKeyOf = <Key extends string>(key: Key): CommentKeyOf<Key> =>
  `${key}${COMMENT_SUFFIX}`;

/** Prose an assessor wanted to say about another item's answer. */
export const comment = z.string().max(COMMENT_MAX_LENGTH);

/**
 * The schema of one answer: one response, in an object so a later fact about
 * the answer can sit beside it without making any document already stored
 * invalid.
 *
 * The shape is the same for every item of every instrument, a comment included.
 * Every published instrument's digest depends on this, so a change here changes
 * every instrument's name. That is the pin test's to catch, and it will: the
 * point of the digest is that a change nobody intended cannot pass quietly.
 */
const answerOf = <Response extends z.ZodType>(response: Response) =>
  z.object({ response }).strict();

/** The comment on the item keyed `key`, which an assessor may always leave out. */
const commentOn = (key: string) =>
  answerOf(comment)
    .meta({
      [SUPPLEMENT_KEYWORD]: { to: key, as: "comment" } satisfies Supplement,
    })
    .optional();

type Comment = ReturnType<typeof commentOn>;

/**
 * An item being declared, built into the entries its group spreads. Every step
 * returns a new builder, so one held in a constant can serve several versions.
 *
 * What it builds is plain zod, which is all an instrument is: the builder saves
 * writing the shape out, and adds nothing a schema does not say. A builder
 * spread without `build()` is refused by the linter, which forbids spreading a
 * class instance. Its state is in ordinary properties so that, past the linter,
 * zod still refuses the values that are not schemas the first time the schema
 * is used, publishing included, where private fields would make the item vanish
 * silently.
 */
export class ItemBuilder<
  Key extends string,
  Item extends z.ZodType,
  Commented extends boolean,
> {
  readonly key: Key;
  readonly schema: Item;
  readonly commented: Commented;

  constructor(key: Key, schema: Item, commented: Commented) {
    this.key = key;
    this.schema = schema;
    this.commented = commented;
  }

  /** The item may be left out of a document. Its comment always may. */
  optional(): ItemBuilder<Key, z.ZodOptional<Item>, Commented> {
    return new ItemBuilder(this.key, this.schema.optional(), this.commented);
  }

  /**
   * Declares the comment an assessor may leave about the item: an answer of
   * its own, keyed by the item's key and `COMMENT_SUFFIX`.
   *
   * Declaring a comment creates a key, which is permanent, so an item takes
   * one only where its module says so. Declare one wherever any collector or
   * historical source might carry one, since a collector can offer a comment
   * box only where the instrument accepts the comment.
   */
  withComment(): ItemBuilder<Key, Item, true> {
    return new ItemBuilder(this.key, this.schema, true);
  }

  /** The item, and any comment on it, each under its own key. */
  build(): Record<Key, Item> &
    (Commented extends true ? Record<CommentKeyOf<Key>, Comment> : unknown) {
    const entries = this.commented
      ? {
          [this.key]: this.schema,
          [commentKeyOf(this.key)]: commentOn(this.key),
        }
      : { [this.key]: this.schema };

    // A computed key widens to `string`, so the entries are typed by what they
    // are built from.
    return entries as Record<Key, Item> &
      (Commented extends true ? Record<CommentKeyOf<Key>, Comment> : unknown);
  }
}

/** An item keyed `key`, answered by `response`, with what it means where its module says. */
export const item = <Key extends string, Response extends z.ZodType>(
  key: Key,
  response: Response,
  meaning?: ItemMeaning<z.infer<Response>>,
) => {
  const schema = answerOf(response);

  return new ItemBuilder(
    key,
    meaning === undefined
      ? schema
      : schema.meta({ [MEANING_KEYWORD]: meaning }),
    false,
  );
};

/**
 * A group with its meaning declared, as an item's is through `item`.
 *
 * Typed as the group it was given, so declaring a meaning changes nothing
 * about what the group accepts.
 */
export const withMeaning = <Group extends z.ZodType>(
  group: Group,
  meaning: Meaning,
): Group => group.meta({ [MEANING_KEYWORD]: meaning });

/**
 * A group with its category declared, typed as the group it was given, so
 * declaring a category changes nothing about what the group accepts.
 *
 * Declare one on every group of an instrument or on none;
 * `parsePublishedInstrumentSchema` refuses a mix.
 */
export const withCategory = <Group extends z.ZodType>(
  group: Group,
  category: GroupCategory,
): Group => group.meta({ [CATEGORY_KEYWORD]: category });
